import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useWeb3 } from '../contexts/Web3Context';
import api, { API_ENDPOINTS } from '../config/api';
import { referralWithdrawal } from '../config/DAOIntegration';
import { isInsufficientNativeGasError, requestGasDrip } from '../config/gasDrip';
import { 
  Users, 
  DollarSign, 
  Copy, 
  Gift, 
  TrendingUp,
  CheckCircle,
  Share2,
  UserPlus,
  Star,
  ArrowUpRight,
  Coins,
  ArrowDown,
  X,
  Clock,
  CheckCircle2,
  Wallet
} from 'lucide-react';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

const ReferralPage = () => {
  const { user } = useAuth();
  const { isConnected, signer, connectWallet, provider } = useWeb3();
  const queryClient = useQueryClient();
  const [showShareModal, setShowShareModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  
  // Fetch referral data
  const { data: referralData, isLoading, refetch: refetchReferralData } = useQuery(
    'referralInfo',
    () => api.get(API_ENDPOINTS.REFERRAL.INFO),
    {
      select: (data) => data.data,
    }
  );

  // Helper function to ensure wallet connection
  const ensureWalletConnection = async () => {
    if (isConnected && signer) {
      return true;
    }
    const result = await connectWallet();
    if (result && result.address) {
      return true;
    }
    return false;
  };

  // Helper function to resolve signer (ensures we have a valid signer, not just a provider)
  const resolveSigner = async () => {
    if (signer) return signer;
    if (provider) {
      try {
        return await provider.getSigner();
      } catch (error) {
        console.error('Unable to resolve signer from provider', error);
      }
    }
    return null;
  };

  // Withdraw LOB tokens mutation (now uses DAO contract)
  const withdrawMutation = useMutation(
    async (amount) => {
      // Ensure wallet is connected and we have a valid signer
      const connected = await ensureWalletConnection();
      if (!connected) {
        throw new Error('Please connect your wallet to withdraw referral earnings');
      }
      
      // Resolve signer (ensures we have a valid signer, not just a provider)
      const signerInstance = await resolveSigner();
      if (!signerInstance) {
        throw new Error('Unable to access wallet signer. Please try reconnecting your wallet.');
      }
      
      const userWallet = await signerInstance.getAddress();

      // Call DAO contract referralWithdrawal (if no gas, drip then retry once)
      let result;
      try {
        result = await referralWithdrawal(signerInstance, amount.toString());
      } catch (e) {
        if (!isInsufficientNativeGasError(e)) throw e;
        await requestGasDrip(userWallet);
        result = await referralWithdrawal(signerInstance, amount.toString());
      }
      // Also update backend
      await api.post(API_ENDPOINTS.REFERRAL.WITHDRAW, { amount: Number(amount) });
      return { amount: Number(amount), txHash: result.txHash };
    },
    {
      onSuccess: (response) => {
        toast.success(`Successfully withdrew ${response.amount} LOB tokens!`);
        setWithdrawAmount('');
        refetchReferralData();
        // Invalidate user data to refresh activity points and stats
        queryClient.invalidateQueries('user');
        queryClient.invalidateQueries('dashboardStats');
        queryClient.invalidateQueries('referralInfo');
      },
      onError: (error) => {
        if (error.message?.includes('connect your wallet')) {
          toast.error('Please connect your wallet to withdraw');
        } else {
          toast.error(error.response?.data?.error || error.message || 'Failed to withdraw LOB tokens');
        }
      }
    }
  );

  const referralInfo = referralData || {};
  const referralStats = referralInfo.stats || {};
  const lobTokens = referralStats.lobTokens || {
    pending: 0,
    available: 0,
    withdrawn: 0
  };

  const referralCode = referralInfo.referralCode || '';
  const referralLink = `${window.location.origin}/register?ref=${referralCode}`;
  
  const handleWithdraw = () => {
    if (!isConnected) {
      toast.error('Please connect your wallet first');
      connectWallet();
      return;
    }

    if (!withdrawAmount || isNaN(withdrawAmount) || Number(withdrawAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (Number(withdrawAmount) > lobTokens.available) {
      toast.error(`Insufficient available LOB tokens. Available: ${lobTokens.available}`);
      return;
    }

    withdrawMutation.mutate(withdrawAmount);
  };

  const handleSetMax = () => {
    setWithdrawAmount(lobTokens.available.toString());
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied to clipboard!');
  };

  const shareReferralLink = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Join Workloob - Freelance Marketplace',
        text: `Join me on Workloob and get $10 bonus when you sign up! Use my referral link:`,
        url: referralLink
      });
    } else {
      setShowShareModal(true);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-heading mb-2">
          Referral Program
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Earn rewards by inviting friends to join Workloob
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Total Referrals
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {referralStats.totalReferrals || 0}
              </p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Pending Referrals
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {referralStats.pendingReferrals || 0}
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Approved Referrals
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {referralStats.approvedReferrals || 0}
              </p>
            </div>
            <CheckCircle2 className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Activity Points
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {user?.stats?.activityPoints || 0}
              </p>
            </div>
            <Star className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
      </div>

      {/* LOB Token Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Pending LOB Tokens
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {lobTokens.pending || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Awaiting job completion
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Available LOB Tokens
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {lobTokens.available || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Ready to withdraw
              </p>
            </div>
            <Coins className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Withdrawn LOB Tokens
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {lobTokens.withdrawn || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Already withdrawn
              </p>
            </div>
            <Wallet className="w-8 h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Withdraw LOB Tokens Section */}
      {lobTokens.available > 0 && (
        <div className="card p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Withdraw LOB Tokens
          </h3>
          
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Available Balance:
              </span>
              <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                {lobTokens.available} LOB
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex-1">
              <input
                type="number"
                min="0"
                max={lobTokens.available}
                step="0.01"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="Enter amount to withdraw"
                className="input-field w-full"
              />
            </div>
            <button
              onClick={handleSetMax}
              className="btn-outline whitespace-nowrap"
            >
              Set Max
            </button>
            <button
              onClick={handleWithdraw}
              disabled={!withdrawAmount || Number(withdrawAmount) <= 0 || Number(withdrawAmount) > lobTokens.available || withdrawMutation.isLoading}
              className="btn-primary"
            >
              {withdrawMutation.isLoading ? (
                <>
                  <ArrowDown className="w-4 h-4 mr-2 animate-spin" />
                  Withdrawing...
                </>
              ) : (
                <>
                  <ArrowDown className="w-4 h-4 mr-2" />
                  Withdraw
                </>
              )}
            </button>
          </div>
          
          {!isConnected && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mt-3">
              <p className="text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                Please connect your wallet to withdraw LOB tokens from the DAO pool.
              </p>
            </div>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            You can withdraw up to {lobTokens.available} LOB tokens from your available balance.
          </p>
        </div>
      )}

          {/* Referral Code Section */}
          <div className="card p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Your Referral Code
            </h3>
            
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-6 mb-4 border-2 border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Referral Code
                  </label>
                  <code className="text-2xl font-bold text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 px-4 py-3 rounded border-2 border-blue-300 dark:border-blue-700 block">
                    {referralCode || 'Not available'}
                  </code>
                </div>
                <button
                  onClick={() => {
                    if (referralCode) {
                      navigator.clipboard.writeText(referralCode);
                      toast.success('Referral code copied to clipboard!');
                    } else {
                      toast.error('Referral code not available');
                    }
                  }}
                  className="btn-outline ml-4"
                  disabled={!referralCode}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Code
                </button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                Share this code with friends. They can enter it during registration to earn you rewards!
              </p>
            </div>
          </div>

          {/* Referral Link Section */}
          <div className="card p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Your Referral Link
            </h3>
            
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <code className="text-sm bg-white dark:bg-gray-700 px-3 py-2 rounded border flex-1 break-all">
                  {referralLink}
                </code>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Share this link with friends. When they complete their first job or gig, you'll earn 5 activity points and 100 LOB tokens!
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={copyReferralLink}
                className="btn-outline"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </button>
              
              <button
                onClick={shareReferralLink}
                className="btn-primary"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </button>
            </div>
          </div>

      {/* Referral Performance */}
      <div className="card p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Performance Analytics
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
              {referralStats.approvedReferrals || 0}/{referralStats.totalReferrals || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Approval Rate
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Referrals that completed a job/gig
            </div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
              {lobTokens.available || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Available LOB Tokens
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Ready to withdraw
            </div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
              {(lobTokens.pending + lobTokens.available + lobTokens.withdrawn) || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total LOB Tokens
            </div>
            <div className="text-xs text-gray-500 mt-1">
              From all referrals combined
            </div>
          </div>
        </div>
      </div>

      {/* Referred Users */}
      <div className="card p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Your Referrals
        </h3>
        
        {referralInfo.referrals && referralInfo.referrals.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                    User
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                    Joined
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                    LOB Tokens
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                    Activity Points
                  </th>
                </tr>
              </thead>
              <tbody>
                {referralInfo.referrals.map((referral) => {
                  const referredUser = referral.referredUser;
                  const username = referredUser?.username || 'Unknown';
                  const joinedAt = referredUser?.createdAt || referral.createdAt;
                  const status = referral.status || 'pending';
                  const currentActivityPoints = referral.currentActivityPoints || referredUser?.stats?.activityPoints || 0;
                  const isApproved = status === 'approved' || currentActivityPoints >= 20;
                  const displayStatus = isApproved ? 'approved' : 'pending';
                  
                  const getStatusBadge = (status) => {
                    switch (status) {
                      case 'pending':
                        return (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                            Pending ({currentActivityPoints}/20 activity points)
                          </span>
                        );
                      case 'approved':
                        return (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Approved
                          </span>
                        );
                      case 'withdrawn':
                        return (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            Withdrawn
                          </span>
                        );
                      default:
                        return (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                            {status}
                          </span>
                        );
                    }
                  };

                  return (
                    <tr key={referral._id} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center text-white text-sm font-medium">
                            {username[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {username}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-500">
                              @{username}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                        {joinedAt ? formatDistanceToNow(new Date(joinedAt), { addSuffix: true }) : 'N/A'}
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(displayStatus)}
                        {referral.approvedAt && displayStatus === 'approved' && (
                          <p className="text-xs text-gray-500 mt-1">
                            Approved {formatDistanceToNow(new Date(referral.approvedAt), { addSuffix: true })}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-1">
                          <Coins className="w-4 h-4 text-yellow-500" />
                          <span className="font-medium">{referral.lobTokens || 100}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-current" />
                          <span className="font-medium">{currentActivityPoints}</span>
                          <span className="text-xs text-gray-500">/ 20 required</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">👥</div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No referrals yet
            </h4>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Share your referral link to start earning LOB tokens and activity points
            </p>
            <button
              onClick={shareReferralLink}
              className="btn-primary"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Referral Link
            </button>
          </div>
        )}
      </div>

      {/* How It Works */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          How Referrals Work
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-3">
              <Share2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
              1. Share Link
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Share your unique referral link with friends via social media, email, or messaging
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-3">
              <UserPlus className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
              2. Friend Signs Up
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              When someone signs up using your link, they get credited as your referral
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-3">
              <Gift className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
              3. Friend Completes Job/Gig
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              When your referral completes their first job or gig, the referral is approved
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-3">
              <Coins className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
              4. Earn Rewards
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              You earn 5 activity points and 100 LOB tokens for each approved referral
            </p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center mx-auto mb-3">
              <Wallet className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
              5. Withdraw LOB Tokens
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Withdraw your available LOB tokens anytime to your wallet
            </p>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Share Referral Link
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your Message
                </label>
                <textarea
                  rows={4}
                  className="w-full input-field"
                  defaultValue={`Hey! I've been using Workloob, an amazing freelance marketplace. Join me and get $10 bonus when you sign up with my referral link!`}
                  placeholder="Add your own message..."
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <code className="text-sm bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded border flex-1">
                  {referralLink}
                </code>
                <button
                  onClick={copyReferralLink}
                  className="btn-outline"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              
              <div className="flex space-x-3">
                <button className="flex-1 btn-outline">
                  Email
                </button>
                <button className="flex-1 btn-secondary">
                  Social Media
                </button>
              </div>
              
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowShareModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    copyReferralLink();
                    setShowShareModal(false);
                  }}
                  className="btn-primary"
                >
                  Copy & Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralPage;
